import React, { useEffect, useState } from 'react';
import { useCheckoutState } from '~/features/checkout/hooks/useCheckoutState';
import { DeliveryMethod } from '~/features/checkout/models/checkout';
import { DropPointDto } from '~/lib';
import { ShippingMethod } from '~/lib/data-contract/salesforce';
import { InputSpinner } from '~/shared/components/form/components/controllable-elements/input-element/styled';
import { DeliveryAddressList } from '../../../DeliveryAddressList';
import { DeliveryAddressMap } from '../../../DeliveryAddressMap';
import { DeliveryAddressSummary } from '../../../DeliveryAddressSummery/DeliveryAddressSummary';
import { useTranslation } from '~/shared/utils/translation';
import {
    StyledMessage,
    ErrorMessage,
    StyledDropPointWrapper,
    DropPointCloseIcon,
    DropPointCloseWrapper,
    StyledTitle,
    StyledSubTitle,
    StyledDeliveryAddressSummary,
} from './styled';
import Loading from '~/app/(preference-center)/preference-center/[id]/loading';
import { useCheckout } from '~/features/checkout/hooks';
import { StyledDeliveryBackIcon } from '../../styled';

type Props = {
    handleCloseDrawer: () => void;
    optionList?: ShippingMethod[];
    setCarrier: (option: ShippingMethod, method?: DeliveryMethod) => void;
    selectedCarrier?: ShippingMethod;
    mapMarkers?: DropPointDto[];
    error?: unknown;
};

export const DeliveryContentDroppoint = ({
    optionList,
    setCarrier,
    mapMarkers,
    selectedCarrier,
    error,
    handleCloseDrawer,
}: Props) => {
    const { vendorSelectedDroppoint, setDeliveryAddress } = useCheckoutState();
    const { dropPointIsFetching, setShippingAddressInformation } = useCheckout();
    const { translate } = useTranslation();
    const [showAddressMap, setShowAddressMap] = useState(false);

    useEffect(() => {
        if (optionList?.length === 1) {
            const [option] = optionList ?? [];
            if (option) {
                setCarrier(option, 'dropPoint');
            }
        }
    }, [optionList]);

    const saveDeliveryInformation = (deliveryAddress: any) => {
        if (vendorSelectedDroppoint && selectedCarrier) {
            setShippingAddressInformation();
        } else {
            setDeliveryAddress(deliveryAddress);
        }

        setShowAddressMap(false);

        //setTimeout here will delay the closing of drawer a little bit
        //to restore focus on the edit button.
        setTimeout(() => {
            handleCloseDrawer();
        });
    };

    const selectCarrier = (carrier: ShippingMethod) => {
        setCarrier(carrier);
        setDeliveryAddress(null);
        setShowAddressMap(true);
    };

    return (
        <StyledDropPointWrapper>
            <DropPointCloseWrapper>
                {showAddressMap && (
                    <StyledDeliveryBackIcon
                        style={{ marginRight: 'auto' }}
                        onClick={() => setShowAddressMap(false)}
                        tabIndex={0}
                        role="button"
                        onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setShowAddressMap(false);
                            }
                        }}
                    />
                )}

                <DropPointCloseIcon
                    data-testid="close-drop-point-icon"
                    onClick={handleCloseDrawer}
                    tabIndex={0}
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleCloseDrawer();
                        }
                    }}
                />
            </DropPointCloseWrapper>

            {!showAddressMap && (
                <StyledTitle>{translate('checkout.delivery.chooseSupplier')}</StyledTitle>
            )}

            <StyledSubTitle>{translate('checkout.delivery.preferedSupplier')}</StyledSubTitle>

            {!showAddressMap && (
                <DeliveryAddressList optionList={optionList} setCarrier={selectCarrier} />
            )}
            {vendorSelectedDroppoint && showAddressMap ? (
                <div>
                    <StyledMessage as="p" variant="bodySmall">
                        {translate('checkout.vendorSelectedDroppoint')}
                    </StyledMessage>
                    <StyledDeliveryAddressSummary>
                        <DeliveryAddressSummary />
                    </StyledDeliveryAddressSummary>
                </div>
            ) : (
                <>
                    {error && (
                        <ErrorMessage>{translate('message.generalErrorMessage')}</ErrorMessage>
                    )}

                    {!dropPointIsFetching && mapMarkers && showAddressMap && (
                        <DeliveryAddressMap
                            optionList={mapMarkers}
                            selectOptionShopDelivery={saveDeliveryInformation}
                            selectedCarrier={selectedCarrier}
                        />
                    )}

                    {dropPointIsFetching && showAddressMap && <Loading />}
                </>
            )}
            {!mapMarkers && selectedCarrier?.dropPoint && (
                <div style={{ marginTop: '1rem' }}>
                    <p>{translate('checkout.selectedDroppoint')}</p>
                    <InputSpinner />
                </div>
            )}
        </StyledDropPointWrapper>
    );
};
